import React from 'react';
import {
  Group, NumberInput, Slider, Stack, Switch, Text,
} from '@mantine/core';
import InspectorRow from '../../../puck/fields/InspectorRow';
import { useSelectedItemProps } from '../../../puck/fields/useSelectedItemProps';
import { useThemeLockedProp } from '../../../puck/fields/themedFieldValues';
import StyleDefaultMark from '../../../puck/fields/StyleDefaultMark';
import {
  CAROUSEL_DWELL_DEFAULT_MS,
  CAROUSEL_DWELL_MAX_MS,
  CAROUSEL_DWELL_MIN_MS,
  CAROUSEL_SCROLL_SPEED_DEFAULT,
  CAROUSEL_SCROLL_SPEED_MAX,
  CAROUSEL_SCROLL_SPEED_MIN,
  CAROUSEL_SCROLL_SPEED_STEP,
} from './carouselLayout';

function ToggleRow({
  label, description, checked, onChange, disabled,
}) {
  return (
    <Switch
      size="sm"
      label={label}
      description={description}
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.currentTarget.checked)}
    />
  );
}

/**
 * Champ composite « Lecture & navigation » : les sept réglages de lecture
 * étaient sept champs à plat dans la sidebar, dont deux nombres bruts
 * (délai, vitesse). Le champ édite `autoplay` ; les props voisines sont
 * écrites via dispatch `replace` — pattern AppearAnimationField.
 */
export default function CarouselPlaybackField({ value, onChange }) {
  const { itemProps, updateItemProps } = useSelectedItemProps();
  const isLocked = useThemeLockedProp('carousel');

  const autoplay = !!value;
  const speed = Number(itemProps.scrollSpeed) || CAROUSEL_SCROLL_SPEED_DEFAULT;
  // Le ruban continu ne s'arrête jamais : pas de temps d'arrêt à régler, et
  // c'est lui seul qui impose la boucle.
  const isContinuous = !!itemProps.maxSlidesVisible;
  const dwellMs = Number(itemProps.autoplayDelay) || CAROUSEL_DWELL_DEFAULT_MS;

  return (
    <Stack gap={12}>
      {!isLocked('autoplay') && (
        <ToggleRow
          label="Lecture automatique"
          checked={autoplay}
          onChange={onChange}
        />
      )}
      {autoplay && (
        <>
          {!isContinuous && (
            <StyleDefaultMark propKey="autoplayDelay">
              <NumberInput
                size="xs"
                label="Temps d'arrêt sur chaque slide"
                description="Durée pendant laquelle une slide reste affichée."
                value={dwellMs / 1000}
                onChange={(next) => updateItemProps({
                  autoplayDelay: Math.round((Number(next) || 0) * 1000),
                })}
                min={CAROUSEL_DWELL_MIN_MS / 1000}
                max={CAROUSEL_DWELL_MAX_MS / 1000}
                step={0.5}
                suffix=" s"
                decimalScale={1}
                clampBehavior="strict"
              />
            </StyleDefaultMark>
          )}
          <StyleDefaultMark propKey="scrollSpeed">
            <InspectorRow
              label={isContinuous ? 'Vitesse de défilement' : 'Vitesse de transition'}
              layout="stacked"
            >
              <Slider
                size="sm"
                min={CAROUSEL_SCROLL_SPEED_MIN}
                max={CAROUSEL_SCROLL_SPEED_MAX}
                step={CAROUSEL_SCROLL_SPEED_STEP}
                value={speed}
                onChange={(next) => updateItemProps({ scrollSpeed: next })}
                label={null}
              />
              <Group justify="space-between" mt={4}>
                <Text size="10px" c="dimmed">Lente</Text>
                <Text size="10px" c="dimmed">Rapide</Text>
              </Group>
            </InspectorRow>
          </StyleDefaultMark>
          {!isLocked('autoplayPauseOnHover') && (
            <ToggleRow
              label="Pause au survol"
              description="Le défilement s'arrête quand la souris passe sur le carrousel."
              checked={itemProps.autoplayPauseOnHover !== false}
              onChange={(next) => updateItemProps({ autoplayPauseOnHover: next })}
            />
          )}
        </>
      )}
      {!isLocked('loop') && (
        <ToggleRow
          label="Boucle infinie"
          description={autoplay && isContinuous
            ? 'Imposée par le défilement continu : sans boucle le ruban s\'arrête sur la dernière slide.'
            : 'Après la dernière slide, on revient à la première.'}
          checked={autoplay && isContinuous ? true : itemProps.loop !== false}
          disabled={autoplay && isContinuous}
          onChange={(next) => updateItemProps({ loop: next })}
        />
      )}
      {!isLocked('controls') && (
        <ToggleRow
          label="Flèches de navigation"
          checked={itemProps.controls !== false}
          onChange={(next) => updateItemProps({ controls: next })}
        />
      )}
      {!isLocked('indicators') && (
        <ToggleRow
          label="Points de navigation"
          description="Les pastilles sous le carrousel indiquent la slide courante."
          checked={itemProps.indicators !== false}
          onChange={(next) => updateItemProps({ indicators: next })}
        />
      )}
    </Stack>
  );
}
